import { useCallback, useEffect, useRef, useState } from "react";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import MicRoundedIcon from "@mui/icons-material/MicRounded";
import { AudioVisualizer, useAudioAnalysis } from "./AudioVisualizer";
import { RecorderActionButton } from "./RecorderActionButton";
import { customPalette } from "../theme/palette";

const RECORD_BUTTON_SIZE = 80;

function formatElapsedTime(elapsedSeconds: number): string {
  const mins = Math.floor(elapsedSeconds / 60);
  const seconds = Math.floor(elapsedSeconds % 60);
  return `${mins.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
}

const DEFAULT_MAX_RECORDING_SECONDS = 90;

export function deriveRecordingDurationMs(
  recordingStartedAtMs: number | null,
  elapsedSeconds: number,
  nowMs: number = Date.now(),
): number {
  const elapsedDurationMs = Math.max(0, elapsedSeconds) * 1000;
  if (!recordingStartedAtMs || nowMs <= recordingStartedAtMs) {
    return elapsedDurationMs;
  }

  return Math.max(elapsedDurationMs, nowMs - recordingStartedAtMs);
}

type AudioCaptureRecorderProps = {
  recordingSeconds?: number;
  onReadyChange?: (isReady: boolean) => void;
  onRecordingChange?: (isRecording: boolean) => void;
  onRecordingComplete?: (params: {
    blob: Blob;
    mimeType: string;
    durationMs: number;
  }) => void | Promise<void>;
  showTimer?: boolean;
};

export function AudioCaptureRecorder({
  recordingSeconds = DEFAULT_MAX_RECORDING_SECONDS,
  onReadyChange,
  onRecordingChange,
  onRecordingComplete,
  showTimer = true,
}: AudioCaptureRecorderProps) {
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const autoStopTimeoutRef = useRef<number | null>(null);
  const timerIntervalRef = useRef<number | null>(null);
  const elapsedSecondsRef = useRef(0);
  const recordingStartedAtMsRef = useRef<number | null>(null);
  const onReadyChangeRef = useRef(onReadyChange);

  const [isRecording, setIsRecording] = useState(false);
  const [isPreparing, setIsPreparing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string>("");
  const [recordingError, setRecordingError] = useState<string>("");
  const [hasSavedRecording, setHasSavedRecording] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [liveRecordingFrequencyLevel, setLiveRecordingFrequencyLevel] =
    useState(0);

  const theme = useTheme();
  const { startAnalysis, stopAnalysis } = useAudioAnalysis({
    onFrequencyData: (level) => {
      setLiveRecordingFrequencyLevel(level);
    },
  });

  const isReady =
    hasSavedRecording && !isRecording && !isPreparing && !isSaving;

  useEffect(() => {
    onReadyChangeRef.current = onReadyChange;
  }, [onReadyChange]);

  const clearTimers = useCallback(() => {
    if (autoStopTimeoutRef.current) {
      window.clearTimeout(autoStopTimeoutRef.current);
      autoStopTimeoutRef.current = null;
    }
    if (timerIntervalRef.current) {
      window.clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
  }, []);

  const stopActiveStream = useCallback(() => {
    if (!streamRef.current) {
      return;
    }
    streamRef.current.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  const stopRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state === "inactive") {
      return;
    }
    recorder.stop();
  }, []);

  const startRecording = useCallback(async () => {
    setRecordingError("");
    setHasSavedRecording(false);
    setIsPreparing(true);
    setIsSaving(false);
    elapsedSecondsRef.current = 0;
    setElapsedSeconds(0);
    setLiveRecordingFrequencyLevel(0);
    recordingStartedAtMsRef.current = null;
    chunksRef.current = [];

    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      setAudioUrl("");
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const recorder = new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (event: BlobEvent) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        clearTimers();
        stopAnalysis();
        setIsRecording(false);
        setElapsedSeconds(0);
        setLiveRecordingFrequencyLevel(0);

        const mimeType = recorder.mimeType || "audio/webm";
        const blob = new Blob(chunksRef.current, { type: mimeType });
        chunksRef.current = [];
        if (blob.size > 0) {
          setAudioUrl(URL.createObjectURL(blob));
          const durationMs = deriveRecordingDurationMs(
            recordingStartedAtMsRef.current,
            elapsedSecondsRef.current,
          );
          if (onRecordingComplete) {
            setIsSaving(true);
            Promise.resolve(
              onRecordingComplete({
                blob,
                mimeType,
                durationMs,
              }),
            )
              .then(() => {
                setHasSavedRecording(true);
                setRecordingError("");
              })
              .catch((error: unknown) => {
                setHasSavedRecording(false);
                setRecordingError(
                  error instanceof Error
                    ? error.message
                    : "Unable to upload recording.",
                );
              })
              .finally(() => {
                setIsSaving(false);
              });
          } else {
            setHasSavedRecording(true);
          }
        } else {
          setHasSavedRecording(false);
        }

        stopActiveStream();
      };

      recorder.start();
      startAnalysis(stream);
      recordingStartedAtMsRef.current = Date.now();
      setIsRecording(true);
      setIsPreparing(false);

      autoStopTimeoutRef.current = window.setTimeout(() => {
        stopRecording();
      }, recordingSeconds * 1000);
    } catch (error: unknown) {
      setIsPreparing(false);
      stopActiveStream();
      setRecordingError(
        error instanceof Error
          ? error.message
          : "Unable to access microphone. Please check browser permissions.",
      );
    }
  }, [
    recordingSeconds,
    audioUrl,
    clearTimers,
    onRecordingComplete,
    stopActiveStream,
    stopRecording,
    startAnalysis,
    stopAnalysis,
  ]);

  useEffect(() => {
    onReadyChangeRef.current?.(isReady);
  }, [isReady]);

  useEffect(() => {
    onRecordingChange?.(isRecording);
  }, [isRecording, onRecordingChange]);

  useEffect(() => {
    if (!isRecording) {
      return;
    }

    const interval = window.setInterval(() => {
      setElapsedSeconds((previous) => previous + 1);
      elapsedSecondsRef.current += 1;
    }, 1000);

    return () => {
      window.clearInterval(interval);
    };
  }, [isRecording]);

  useEffect(() => {
    return () => {
      clearTimers();
      stopAnalysis();
      stopRecording();
      stopActiveStream();
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl, clearTimers, stopActiveStream, stopAnalysis, stopRecording]);

  return (
    <Stack spacing={2} alignItems="center">
      {showTimer && (
        <Box
          marginBottom={1}
          display="flex"
          flexDirection="row"
          justifyContent="center"
          alignItems="center"
        >
          <Typography
            role="timer"
            aria-label="Elapsed Recording Time"
            variant="body1"
            sx={{ color: theme.palette.text.primary }}
          >
            {formatElapsedTime(elapsedSeconds)}
          </Typography>
        </Box>
      )}

      {isRecording ? (
        <AudioVisualizer
          frequencyLevel={liveRecordingFrequencyLevel}
          color={theme.palette.primary.main}
          buttonSize="large"
          buttonWidth={RECORD_BUTTON_SIZE}
          buttonHeight={RECORD_BUTTON_SIZE}
          onStop={stopRecording}
          customMargin={-16}
        />
      ) : (
        <RecorderActionButton
          onClick={startRecording}
          disabled={isPreparing || isSaving || hasSavedRecording}
          isBusy={isPreparing || isSaving}
          isRecording={false}
          hasSavedAttempt={hasSavedRecording}
        >
          <MicRoundedIcon
            sx={{ fontSize: 36, color: customPalette.gray["0"] }}
          />
        </RecorderActionButton>
      )}
      <Typography variant={"body1"}>
        {!hasSavedRecording && isRecording
          ? `Recording...`
          : isSaving
            ? "Uploading recording..."
            : "Press to record"}
      </Typography>
      {recordingError ? (
        <Typography variant={"body1"} color="error">
          {recordingError}
        </Typography>
      ) : null}
      {audioUrl ? (
        <Typography variant="caption" color="text.secondary">
          {isSaving
            ? "Uploading to secure storage."
            : hasSavedRecording
              ? `Recording saved. Click Next.`
              : "Recording ready."}
        </Typography>
      ) : null}
    </Stack>
  );
}
